/**
 * The widget API surface, taken from `@axon/contracts/routes/widget` and NOT
 * restated here.
 *
 * Every type below is `z.infer` of a schema the API validates against. That is
 * the point of the contracts package (ADR-0004): there is one description of the
 * wire, the server enforces it, and a client that disagrees fails to compile
 * rather than in production. Paths are literals checked against the same
 * objects at compile time — see `WIDGET_ROUTE`.
 *
 * WHAT THIS FILE ADDS is the few things the schemas cannot say, each of which is
 * a rendering decision rather than a protocol one:
 *   - `KNOWN_PRECHAT_TYPES`, the field types this app knows how to draw. The wire
 *     type is an OPEN string, deliberately, so this list is a rendering table and
 *     never a validator.
 *   - `SeqString`, a name for the fact that `seq` is an int8 carried as a decimal
 *     string, so it is compared as a BigInt and never as a Number.
 */
import type { z } from 'zod';

import type {
  closeWidgetConversation,
  createWidgetSession,
  endWidgetSession,
  getWidgetConversation,
  getWidgetConversationById,
  listWidgetConversations,
  putWidgetVisitorContext,
  sendWidgetMessage,
  WidgetAppearanceSchema,
  WidgetConversationListSchema,
  WidgetConversationSchema,
  WidgetConversationSummarySchema,
  WidgetConversationThreadSchema,
  WidgetMessageSchema,
  WidgetPrechatFieldSchema,
  WidgetSendMessageRequestSchema,
  WidgetSendMessageResultSchema,
  WidgetSessionEndedSchema,
  WidgetSessionRequestSchema,
  WidgetSessionSchema,
  WidgetThreadSchema,
  WidgetVisitorContextResultSchema,
  WidgetVisitorContextSchema,
} from '@axon/contracts/routes/widget';

/**
 * The paths, as literals CHECKED against the route objects by the compiler.
 *
 * THE IMPORT ABOVE IS TYPE-ONLY ON PURPOSE (P1-017, 2026-10-29). Importing the
 * route objects as VALUES pulled the whole contracts module into the bundle,
 * and with it zod: the schemas are built when the module loads, although this
 * app never calls `.parse`. The zod 4.6 security upgrade made that dead weight
 * 22 KB heavier and pushed the bundle over its size budget. `defineRoute` keeps
 * each `path` as a literal type, so `satisfies` turns a renamed route into a
 * compile error here — the same guarantee as before, without the runtime.
 */
export const WIDGET_ROUTE = {
  session: { path: '/v1/widget/session' satisfies (typeof createWidgetSession)['path'] },
  /** ADR-0249 §4: the site's player signed out. */
  endSession: { path: '/v1/widget/session/end' satisfies (typeof endWidgetSession)['path'] },
  conversation: {
    path: '/v1/widget/conversation' satisfies (typeof getWidgetConversation)['path'],
  },
  sendMessage: { path: '/v1/widget/messages' satisfies (typeof sendWidgetMessage)['path'] },
  visitorContext: {
    path: '/v1/widget/context' satisfies (typeof putWidgetVisitorContext)['path'],
  },
  /** P2-52: the visitor's own history, and the read of one row of it. */
  conversations: {
    path: '/v1/widget/conversations' satisfies (typeof listWidgetConversations)['path'],
  },
  conversationById: {
    path: '/v1/widget/conversations/{conversationId}' satisfies (typeof getWidgetConversationById)['path'],
  },
  closeConversation: {
    path: '/v1/widget/conversation/close' satisfies (typeof closeWidgetConversation)['path'],
  },
} as const;

export type WidgetAppearance = z.infer<typeof WidgetAppearanceSchema>;
export type WidgetPrechatField = z.infer<typeof WidgetPrechatFieldSchema>;
export type WidgetMessage = z.infer<typeof WidgetMessageSchema>;
export type WidgetConversation = z.infer<typeof WidgetConversationSchema>;
export type WidgetSession = z.infer<typeof WidgetSessionSchema>;
export type WidgetThread = z.infer<typeof WidgetThreadSchema>;
/**
 * One row of the visitor's history (P2-52) — a `WidgetConversation` plus the
 * `preview` a list needs. It is a SUPERSET of the conversation type, which is
 * what lets the history screen compare `summary.id` with the live
 * `conversation.id` and label the row the visitor is already in.
 */
export type WidgetConversationSummary = z.infer<typeof WidgetConversationSummarySchema>;
export type WidgetConversationList = z.infer<typeof WidgetConversationListSchema>;
/** A PAST conversation with its messages. `conversation` is never null here. */
export type WidgetConversationThread = z.infer<typeof WidgetConversationThreadSchema>;
export type WidgetSendMessageResult = z.infer<typeof WidgetSendMessageResultSchema>;
export type WidgetSessionRequest = z.infer<typeof WidgetSessionRequestSchema>;
export type WidgetSessionEnded = z.infer<typeof WidgetSessionEndedSchema>;
export type WidgetSendMessageRequest = z.infer<typeof WidgetSendMessageRequestSchema>;
/**
 * The request body of `widget.context.put`.
 *
 * `z.infer` and NOT a local restatement, and here that choice does something
 * unusually load-bearing: the schema declares `pagePath` and `referrerOrigin`
 * with `.optional()`, this package compiles with `exactOptionalPropertyTypes`,
 * and `buildVisitorContext` spreads those two members CONDITIONALLY. So the
 * builder's return type assigns to this one only because both agree that an
 * absent key and an explicit `undefined` are different things. A hand-written
 * `pagePath?: string | undefined` here would have compiled and quietly accepted
 * `{ pagePath: undefined }`, which serialises to a body the server reads as a
 * cleared column — the same value a refusal produces, arrived at by accident.
 */
export type WidgetVisitorContextRequest = z.infer<typeof WidgetVisitorContextSchema>;
export type WidgetVisitorContextResult = z.infer<typeof WidgetVisitorContextResultSchema>;

/** An answer to one pre-chat field. Scalars only — the schema allows string | boolean. */
export type PrechatAnswer = NonNullable<WidgetSessionRequest['prechat']>[string];

/**
 * `seq` is an int8 on the wire, carried as a decimal STRING because the values
 * outlive `Number.MAX_SAFE_INTEGER`. This alias exists so that every place the
 * widget orders or compares one has to go looking for `compareSeq` rather than
 * reaching for `<`, which on strings compares lexicographically and puts "9"
 * after "10".
 */
export type SeqString = string;

/**
 * The pre-chat field types this app can draw. NOT a validator and not a closed
 * set on the wire: `type` is an open string precisely because a widget lives in
 * someone else's page and updates when their CDN cache expires. Anything outside
 * this list renders as a text input — see `src/prechat/fields.ts` for why
 * falling back beats skipping.
 */
export const KNOWN_PRECHAT_TYPES = ['text', 'email', 'phone', 'select', 'textarea'] as const;
export type KnownPrechatType = (typeof KNOWN_PRECHAT_TYPES)[number];
